/*
 * SecondLap — défilement continu.
 * Enveloppe une grille .sl-grid et un lien « page suivante » ; quand le lien
 * approche du viewport, la page suivante est récupérée et ses cartes sont
 * ajoutées à la grille. Sans JS, le lien reste une pagination normale.
 *
 * Retour depuis une fiche produit : le navigateur restitue normalement la page
 * entière depuis son bfcache, et ce script ne se ré-exécute même pas. Mais
 * Shopify sert la page en cache-control: no-store, ce qui rend cette
 * restitution fragile ; quand elle échoue, la page revient avec une seule
 * page de produits et la position de défilement devient irrécupérable.
 * D'où le filet ci-dessous : on mémorise le nombre de cartes et la position,
 * et on ne les rejoue que si le bfcache n'a pas fait son travail.
 */
const SL_SCROLL_KEY = 'sl-collection-scroll';
class SLInfiniteScroll extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-sl-grid]');
    this.next = this.querySelector('[data-sl-next]');
    this.end = this.querySelector('[data-sl-end]');
    this.live = this.querySelector('[data-sl-live]');
    this.loading = false;

    if (!this.grid) return;
    if (!this.next) {
      this.finish();
      return;
    }

    this.next.addEventListener('click', (event) => {
      if (this.failed) return; // le lien reprend son rôle de pagination
      event.preventDefault();
      this.load();
    });

    this.saveState = this.saveState.bind(this);
    window.addEventListener('pagehide', this.saveState);

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.load();
        },
        { rootMargin: '600px 0px' }
      );
      this.observer.observe(this.next);
    }

    this.restoreState();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('pagehide', this.saveState);
  }

  get stateKey() {
    return window.location.pathname + window.location.search;
  }

  saveState() {
    if (!this.grid) return;
    try {
      sessionStorage.setItem(
        SL_SCROLL_KEY,
        JSON.stringify({
          url: this.stateKey,
          cartes: this.grid.children.length,
          y: Math.round(window.scrollY),
        })
      );
    } catch (error) {
      // navigation privée ou quota plein : on se passe du filet
    }
  }

  async restoreState() {
    const nav = performance.getEntriesByType('navigation')[0];
    if (nav && nav.type !== 'back_forward') return;

    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(SL_SCROLL_KEY) || 'null');
    } catch (error) {
      return;
    }
    if (!saved || saved.url !== this.stateKey) return;

    // bfcache réussi : la grille est déjà complète, le navigateur a tout remis
    if (this.grid.children.length >= saved.cartes) return;

    let tours = 0;
    while (this.grid.children.length < saved.cartes && this.next && tours < 20) {
      await this.load();
      tours += 1;
    }
    window.scrollTo(0, saved.y);
  }

  async load() {
    if (this.loading || this.failed || !this.next) return;
    const url = this.next.getAttribute('href');
    if (!url) return;

    this.loading = true;
    this.setAttribute('aria-busy', 'true');
    this.next.classList.add('sl-more__btn--loading');

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(response.status);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const items = doc.querySelectorAll('sl-infinite-scroll [data-sl-grid] > li');
      items.forEach((item) => this.grid.appendChild(document.importNode(item, true)));

      const nextUrl = doc.querySelector('sl-infinite-scroll [data-sl-next]')?.getAttribute('href');
      if (nextUrl) {
        this.next.setAttribute('href', nextUrl);
      } else {
        this.finish();
      }

      this.announce(items.length);
    } catch (error) {
      // On coupe l'auto-chargement : le lien redevient une pagination classique.
      console.error('sl-infinite-scroll', error);
      if (this.observer) this.observer.disconnect();
      this.failed = true;
    } finally {
      this.loading = false;
      this.removeAttribute('aria-busy');
      if (this.next) this.next.classList.remove('sl-more__btn--loading');
    }
  }

  finish() {
    if (this.observer) this.observer.disconnect();
    if (this.next) {
      this.next.remove();
      this.next = null;
    }
    if (this.end) this.end.hidden = false;
  }

  announce(count) {
    if (!this.live || !count) return;
    this.live.textContent = `${count} pièces supplémentaires chargées`;
  }
}

if (!customElements.get('sl-infinite-scroll')) {
  customElements.define('sl-infinite-scroll', SLInfiniteScroll);
}
